import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getMovieById, getRelatedMovies, getArticlesByMovie, formatDate } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";

const FAVORITES_KEY = "80s-movie-favorites";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(FAVORITES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
}

export default function MovieDetail() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setFavorites(readFavorites());
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  usePageMeta(
    movie
      ? `${movie.title}（${movie.year}）- 80s电影经典影评与介绍`
      : "电影信息暂缺 - 80s电影",
    movie
      ? `${movie.title}（${movie.year}）影片资料：导演${movie.director}，主演${movie.cast.join("、")}，类型${movie.genres.join("、")}，片长${movie.runtime}分钟。包含剧情概要与编辑影评。`
      : "该影片信息暂缺，可返回电影分类页浏览其他八十年代经典影片。",
    movie
      ? `${movie.title},${movie.year},80年代电影,${movie.genres.join(",")},${movie.director},电影介绍`
      : "80年代电影,经典电影"
  );

  if (!movie) {
    return (
      <div className="mx-auto max-w-page px-4 py-16 md:px-6 md:py-24">
        <EmptyState
          icon="ri-film-line"
          title="电影信息暂缺"
          description="这部电影的档案可能尚未收录，或者链接地址有误。您可以返回分类页继续浏览其他八十年代影片。"
          action={
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-700 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-800"
            >
              返回电影分类
              <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          }
        />
      </div>
    );
  }

  const isFavorite = favorites.includes(movie.id);
  const related = getRelatedMovies(movie, 3);
  const relatedArticles = getArticlesByMovie(movie.id);

  const toggleFavorite = () => {
    const next = isFavorite
      ? favorites.filter((item) => item !== movie.id)
      : [...favorites, movie.id];
    setFavorites(next);
    try {
      window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
    } catch {
      // 本地存储不可用时静默失败，仍保留当前会话状态
    }
    setNotice(isFavorite ? "已从本机收藏移除" : "已加入本机收藏（演示功能）");
  };

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "电影分类", to: "/category" },
              { label: movie.title },
            ]}
          />
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {movie.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category?genre=${encodeURIComponent(genre)}`}
                className="rounded-full border border-primary-200 bg-primary-100/70 px-2.5 py-1 text-[11px] text-primary-800 transition-colors hover:border-primary-400"
              >
                {genre}
              </Link>
            ))}
            <span className="rounded-full bg-accent-100 px-2.5 py-1 font-label text-[11px] tracking-wider text-accent-900">
              {movie.year} 年作品
            </span>
          </div>
          <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-5xl">
            {movie.title}
            <span className="ml-3 align-middle font-label text-base font-normal tracking-[0.1em] text-secondary-500 md:text-lg">
              {movie.year}
            </span>
          </h1>
          <p className="mt-2 font-label text-sm uppercase tracking-[0.2em] text-secondary-600">
            {movie.originalTitle}
          </p>
          <p className="mt-4 max-w-2xl font-heading text-base italic text-foreground-700 md:text-lg">
            「{movie.tagline}」
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="aspect-[3/4] w-full max-w-[320px] overflow-hidden rounded-lg bg-secondary-200 lg:max-w-none">
                <img
                  src={movie.poster}
                  alt={`${movie.title}（${movie.year}）电影海报`}
                  title={`${movie.title} ${movie.year} 经典电影海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={toggleFavorite}
                  className={`flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm transition-colors ${
                    isFavorite
                      ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                      : "border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={isFavorite ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {isFavorite ? "已收藏（本机）" : "收藏这部影片"}
                </button>
                <p className="text-[11px] leading-relaxed text-secondary-500">
                  收藏为前端演示功能，仅保存在当前浏览器的本地存储中，不会上传至服务器。
                </p>
                <Link
                  to="/category"
                  className="mt-1 flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回电影分类
                </Link>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-100/60 p-4">
                <p className="font-label text-[11px] uppercase tracking-[0.2em] text-secondary-600">
                  影片档案编号
                </p>
                <p className="mt-1.5 font-label text-lg tracking-[0.12em] text-primary-700">
                  ARCH-{movie.year}-{String(movie.popularity).padStart(3, "0")}
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-secondary-600">
                  资料由站内编辑整理，仅用于影迷交流，影片版权归原权利人所有。
                </p>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 gap-x-4 gap-y-5 rounded-lg border border-background-200 bg-background-100/40 p-4 sm:grid-cols-3 md:p-5">
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">导演</p>
                <p className="mt-1 text-sm text-foreground-900">{movie.director}</p>
              </div>
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">上映年份</p>
                <p className="mt-1 text-sm text-foreground-900">{movie.year} 年</p>
              </div>
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">片长</p>
                <p className="mt-1 text-sm text-foreground-900">{movie.runtime} 分钟</p>
              </div>
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">类型</p>
                <p className="mt-1 text-sm text-foreground-900">{movie.genres.join(" / ")}</p>
              </div>
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">编辑评分</p>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-foreground-900">
                  <i className="ri-star-fill text-[11px] text-accent-500"></i>
                  {movie.rating.toFixed(1)} / 10
                </p>
              </div>
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">资料完整度</p>
                <p className="mt-1 text-sm text-foreground-900">已收录</p>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <p className="font-label text-[11px] uppercase tracking-[0.18em] text-secondary-600">主演</p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-900">
                  {movie.cast.join(" · ")}
                </p>
              </div>
            </div>

            <section className="mt-9">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                剧情概要
              </h2>
              <div className="mt-3 h-[1px] w-16 bg-accent-500"></div>
              <p className="mt-4 text-sm leading-[1.9] text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>
              <p className="mt-3 text-sm leading-[1.9] text-foreground-600 md:text-base">
                本页资料依据公开影片信息整理，包含上映年份、主创名单与片长等基础事实。若你第一次接触这部作品，可以把它当作观影前的背景阅读；如果你已经看过，也欢迎带着自己的记忆重读一遍。
              </p>
            </section>

            <section className="mt-9 rounded-lg border-l-2 border-accent-500 bg-accent-50/70 p-5 md:p-6">
              <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.22em] text-accent-800">
                <i className="ri-double-quotes-l"></i>
                编辑影评精选
              </p>
              <p className="mt-3 font-heading text-base leading-[1.9] text-foreground-800 md:text-lg">
                「{movie.reviewSnippet}」
              </p>
              <p className="mt-4 text-xs text-secondary-600">—— {movie.reviewAuthor}，80s电影编辑部</p>
            </section>

            {relatedArticles.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  相关影评与资讯
                </h2>
                <div className="mt-4 divide-y divide-background-200 border-y border-background-200">
                  {relatedArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/news/${article.id}`}
                      className="group flex items-center justify-between gap-4 py-3.5"
                    >
                      <div>
                        <p className="text-[11px] text-secondary-500">
                          {article.type} · {formatDate(article.date)}
                        </p>
                        <p className="mt-1 text-sm text-foreground-900 transition-colors group-hover:text-primary-700 md:text-base">
                          {article.title}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line shrink-0 text-secondary-400 group-hover:text-primary-600"></i>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-10">
              <div className="flex items-end justify-between gap-4">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  相关电影推荐
                </h2>
                <Link to="/category" className="text-xs text-primary-700 hover:text-primary-600">
                  查看更多
                </Link>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} showSynopsis={false} />
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-in">
          <div className="flex items-center gap-2 rounded-full bg-primary-900 px-4 py-2.5 text-sm text-background-50 shadow-lg">
            <i className="ri-information-line text-accent-300"></i>
            {notice}
          </div>
        </div>
      )}
    </div>
  );
}